import { Button, Typography } from '@mui/material'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { useNotificationSlice } from 'redux/slices/hooks/useNotificationSlice'
import DeepcellDialog from 'components/shared/DeepcellDialog'
import { DeepcellTextField } from 'components/shared'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import { startTopUpUMAP, getSession } from 'utils/api'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { useCurrentCellCountAfterPrefilters } from './useCurrentCellCountAfterPrefilters'

const POLL_INTERVAL_MS = 3000
const MAX_POLL_RETRIES = 3
const LONG_RUNNING_THRESHOLD_MS = 2 * 60 * 1000

function getProgressTitle(state: TopUpState): string {
  if (state === 'saving') return 'Saving Prefilters...'
  if (state === 'creating') return 'Creating Top-up Session'
  return 'Processing UMAP'
}

function getProgressMessage(state: TopUpState, isLongRunning: boolean): string {
  if (state === 'saving') return 'Saving prefilter changes before top-up...'
  if (state === 'creating') return 'Creating new session...'
  if (isLongRunning) {
    return 'Your dataset is still being processed. You can safely close this dialog — the session will appear in your session list when ready.'
  }
  return 'Computing UMAP. This may take a few minutes...'
}

function formatElapsed(elapsedMs: number): string {
  const seconds = Math.round(elapsedMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (minutes === 0) return `${secs}s`
  return `${minutes}m ${secs}s`
}

interface TopUpUMAPDialogProps {
  open: boolean
  onClose: () => void
  onCancel: () => void
  onSuccess?: () => void
  sessionId: number
  onSaveFirst: () => Promise<number>
}

type TopUpState = 'idle' | 'saving' | 'creating' | 'polling' | 'success'

export const TopUpUMAPDialog = ({
  open,
  onClose,
  onCancel,
  onSuccess,
  sessionId,
  onSaveFirst,
}: TopUpUMAPDialogProps): JSX.Element | null => {
  const [state, setState] = useState<TopUpState>('idle')
  const [sessionName, setSessionName] = useState<string>('')
  const [targetSessionId, setTargetSessionId] = useState<number>(0)
  const [pollingStartTime, setPollingStartTime] = useState<number>(0)
  const [elapsedDisplay, setElapsedDisplay] = useState<string>('')
  const [isLongRunning, setIsLongRunning] = useState(false)
  const { showSuccess, showError } = useNotificationSlice()
  const {
    cellVisualizations: { max_cell_count: maxCellCount },
  } = useCellVisualizationsSlice()
  const currentCount = useCurrentCellCountAfterPrefilters()
  const { updateSessionAndVersion, versionId } = useCellVisualizationUrlParams()
  const queryClient = useQueryClient()
  const eventsManager = useEventManager()

  const delta = useMemo(() => {
    if (!maxCellCount || currentCount >= maxCellCount) return 0
    return maxCellCount - currentCount
  }, [currentCount, maxCellCount])

  const eventParams = useMemo(() => ({
    session_id: sessionId,
    version_id: versionId,
    current_cell_count: currentCount,
    max_cell_count: maxCellCount ?? 0,
    delta,
  }), [sessionId, versionId, currentCount, maxCellCount, delta])

  useEffect(() => {
    if (open && state === 'idle') {
      eventsManager.sendTopUpUmapDialogShown(eventParams)
    }
  }, [open, state, eventsManager, eventParams])

  // Update elapsed time display and long-running flag
  useEffect(() => {
    if (state !== 'polling' || pollingStartTime === 0) {
      setElapsedDisplay('')
      setIsLongRunning(false)
      return undefined
    }
    const interval = setInterval(() => {
      const elapsedMs = Date.now() - pollingStartTime
      setElapsedDisplay(formatElapsed(elapsedMs))
      setIsLongRunning(elapsedMs > LONG_RUNNING_THRESHOLD_MS)
    }, 1000)
    return () => {
      clearInterval(interval)
      setElapsedDisplay('')
      setIsLongRunning(false)
    }
  }, [state, pollingStartTime])

  const queryKey = useMemo(
    () => ['getTopUpSession', targetSessionId],
    [targetSessionId]
  )

  const stopPolling = useCallback(() => {
    setState('idle')
    setSessionName('')
    setTargetSessionId(0)
    setPollingStartTime(0)
    setElapsedDisplay('')
    setIsLongRunning(false)
    queryClient.cancelQueries(queryKey)
  }, [queryClient, queryKey])

  const handleDismiss = useCallback(() => {
    stopPolling()
    onClose()
    showSuccess('Your session is still being processed and will appear in the session list when ready.', 10000)
  }, [stopPolling, onClose, showSuccess])

  useQuery(queryKey, () => getSession(targetSessionId, 1), {
    enabled: state === 'polling' && targetSessionId > 0,
    retry: MAX_POLL_RETRIES,
    refetchInterval: POLL_INTERVAL_MS,
    onError: () => {
      stopPolling()
      onClose()
      showError('Unable to check session status. Please refresh the page and check your session manually.')
    },
    onSuccess: (response) => {
      const sessionStatus = response?.data?.data?.status

      if (sessionStatus === 'ready') {
        // Navigate FIRST, before any state cleanup.
        // Avoid calling stopPolling() here — it resets state to 'idle', which
        // can briefly re-show the confirmation dialog and trigger an unintended
        // handleCancel → performSaveAndContinue navigation via the MUI Dialog onClose.
        const completedSessionId = targetSessionId
        setState('success')
        eventsManager.sendTopUpUmapComplete({
          original_session_id: sessionId,
          new_session_id: completedSessionId,
          version_id: versionId,
          duration_seconds: pollingStartTime > 0 ? Math.round((Date.now() - pollingStartTime) / 1000) : 0,
        })
        showSuccess('Top-up session ready! Navigating to new session...')
        updateSessionAndVersion(completedSessionId, 1)
        onSuccess?.()
        // Clean up polling state after navigation
        setSessionName('')
        setTargetSessionId(0)
        setPollingStartTime(0)
        setElapsedDisplay('')
        setIsLongRunning(false)
        queryClient.cancelQueries(queryKey)
        return
      }

      if (!sessionStatus || sessionStatus === 'accepted' || sessionStatus === 'pending' || sessionStatus === 'processing') {
        // No timeout — keep polling until READY or FAILED
        return
      }

      if (sessionStatus === 'failed') {
        stopPolling()
        onClose()
        showError('Top-up session creation failed. Please try again.')
        return
      }

      stopPolling()
      onClose()
      showError(`Unexpected session status: ${sessionStatus ?? 'unknown'}. Please try again.`)
    },
  })

  const handleConfirm = useCallback(async () => {
    if (!maxCellCount) return

    eventsManager.sendTopUpUmapRequest(eventParams)
    setState('saving')

    let savedVersionId: number
    try {
      savedVersionId = await onSaveFirst()
    } catch (error) {
      setState('idle')
      onClose()
      showError('Failed to save prefilter changes. Please try again.')
      return
    }

    setState('creating')

    try {
      const response = await startTopUpUMAP(sessionId, savedVersionId, maxCellCount, sessionName.trim())
      const createdSessionId = response.data.session_id

      if (!createdSessionId) {
        throw new Error('No session_id returned from top-up API')
      }

      setPollingStartTime(Date.now())
      setTargetSessionId(createdSessionId)
      setState('polling')
    } catch (error) {
      setState('idle')
      onClose()
      showError('Failed to create top-up session. Please try again.')
    }
  }, [sessionId, maxCellCount, sessionName, showError, onSaveFirst, onClose, eventsManager, eventParams])

  const handleCancel = useCallback(() => {
    eventsManager.sendTopUpUmapCancelled(eventParams)
    setState('idle')
    setSessionName('')
    onCancel()
  }, [onCancel, eventsManager, eventParams])

  if (!maxCellCount || currentCount >= maxCellCount) {
    return null
  }

  return (
    <>
      <DeepcellDialog
        open={open && state === 'idle'}
        titleLabel="Recompute UMAP?"
        okLabel="Top up and recompute"
        cancelLabel="Cancel"
        showCancelButton
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        okDisabled={!sessionName.trim()}
        loadingButton={false}
        loadingButtonState={false}
        data-testid="top-up-recompute-dialog"
      >
        <DeepcellTextField
          label="Session Name"
          placeholder="Session Name"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          onFocus={(e) => e.target.select()}
        />
        <Typography component="div" sx={{ mb: 2, mt: 2 }}>
          We&apos;ll add {delta.toLocaleString()} more cells that match your current filters so the UMAP reaches {maxCellCount.toLocaleString()} cells.
        </Typography>
        <Typography component="div" sx={{ mb: 2 }}>
          This will create a <strong>new session</strong> with the recomputed UMAP.
        </Typography>
        <Typography component="div">
          The UMAP layout will be recomputed and the plot may change. Regions and selections may shift.
        </Typography>
      </DeepcellDialog>

      {(state === 'saving' || state === 'creating' || state === 'polling') && (
        <DeepcellDialog
          open
          titleLabel={getProgressTitle(state)}
          showCancelButton={false}
          handleConfirm={() => {}}
          handleCancel={() => {}}
          loadingButton={false}
          loadingButtonState={false}
        >
          <Typography component="div">
            {getProgressMessage(state, isLongRunning)}
          </Typography>
          {state === 'polling' && elapsedDisplay && (
            <Typography component="div" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
              Elapsed: {elapsedDisplay}
            </Typography>
          )}
          {isLongRunning && (
            <Button
              onClick={handleDismiss}
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          )}
        </DeepcellDialog>
      )}
    </>
  )
}

export default TopUpUMAPDialog
